// ============================================================================
// MINIMIZED MODAL STRIP — Icon trigger + dropdown for minimized modals
// ============================================================================
// Renders a single icon button in the ModuleHeader toolbar. When clicked,
// opens a dropdown panel listing minimized modals for the current tab with
// Resume and Discard actions. Zero additional vertical space consumed.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from '../../../contexts/ModalMinimizeContext';

// ============================================================================
// COMPONENT
// ============================================================================

interface MinimizedModalStripProps {
  tabId: string;
}

export const MinimizedModalStrip: React.FC<MinimizedModalStripProps> = ({ tabId }) => {
  const { minimizedModals, restore } = useModalMinimize();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0, maxHeight: 400 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Filter to only modals belonging to this tab (array order is stable — insertion order)
  const tabModals = minimizedModals.filter(m => m.tabId === tabId);

  const updateMenuPos = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
        maxHeight: window.innerHeight - rect.bottom - 12,
      });
    }
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updateMenuPos);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updateMenuPos);
    };
  }, [menuOpen, updateMenuPos]);

  // Close menu when all modals are dismissed/restored
  useEffect(() => {
    if (tabModals.length === 0) setMenuOpen(false);
  }, [tabModals.length]);

  const handleToggle = useCallback(() => {
    if (!menuOpen) updateMenuPos();
    setMenuOpen(prev => !prev);
  }, [menuOpen, updateMenuPos]);

  const handleResume = useCallback((id: string) => {
    restore(id);
    setMenuOpen(false);
  }, [restore]);

  if (tabModals.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        className={`minimized-modal-trigger ${menuOpen ? 'active' : ''}`}
        onClick={handleToggle}
        title={`${tabModals.length} minimized form${tabModals.length > 1 ? 's' : ''}`}
      >
        <IconSystem.chevronUp size={16} />
        <span className="minimized-modal-trigger-count">{tabModals.length}</span>
      </button>

      {menuOpen && createPortal(
        <>
          <div
            onClick={() => setMenuOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 10043, background: 'transparent' }}
          />
          <div
            ref={menuRef}
            className="minimized-modal-menu"
            style={{
              position: 'fixed',
              top: menuPos.top,
              right: menuPos.right,
              zIndex: 10044,
            }}
          >
            <div className="minimized-modal-menu-header">
              <IconSystem.windowRestore size={15} style={{ flexShrink: 0, opacity: 0.7 }} />
              Minimized Screens
            </div>
            <div className="minimized-modal-menu-items" style={{ maxHeight: menuPos.maxHeight - 30 }}>
              {tabModals.map((modal) => (
                <div
                  key={modal.id}
                  className="minimized-modal-menu-item"
                  onClick={() => handleResume(modal.id)}
                  title="Click to resume"
                >
                  <IconSystem.arrowRight size={14} style={{ flexShrink: 0, color: 'var(--primary)' }} />
                  <span className="minimized-modal-menu-title">{modal.title}</span>
                </div>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}

    </>
  );
};

export default MinimizedModalStrip;
